import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Activity } from 'lucide-react';
import { WESTGARD_DESCRIPTIONS, type WestgardRule } from '@his/shared';
import { ApiError, post } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { dateOnly } from '@/lib/utils';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { RecordState } from '@/components/ui/record-state';
import { Alert, DateTimePicker, Field, Input } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { safeReturnTo } from '../opd/BookAppointmentPage';
import { getPages } from './pagedList';
import type { ControlLot } from './QcPage';

function runRefusal(caught: unknown): string {
  if (caught instanceof ApiError && (caught.status === 422 || caught.status === 409)) return caught.message;
  return describeError(caught);
}

function runAtIso(text: string): string | undefined {
  if (!text) return undefined;
  const parsed = new Date(text);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed.toISOString();
}

export function QcRunPage() {
  const [params] = useSearchParams();
  const lotId = params.get('lotId') ?? '';
  const testId = params.get('testId') ?? '';
  const returnTo = safeReturnTo(params.get('returnTo'), `/lab/qc${lotId ? `?lotId=${lotId}` : ''}`);

  const lots = useQuery({
    queryKey: ['qc-lots', testId],
    queryFn: () => getPages<ControlLot>('/lab/qc/lots', { testId: testId || undefined, limit: 200 }),
    enabled: Boolean(lotId),
  });

  const lot = lots.data?.find((row) => row.id === lotId) ?? null;

  if (!lot) {
    return (
      <FormScreen title={tr('lab', 'record_a_control_run')} icon={Activity} backTo={returnTo}>
        <RecordState error={lots.error} missing={!lotId || !lots.isLoading} label={tr('lab', 'loading')} />
      </FormScreen>
    );
  }

  return <RunForm key={lot.id} lot={lot} returnTo={returnTo} />;
}

function RunForm({ lot, returnTo }: { lot: ControlLot; returnTo: string }) {
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [value, setValue] = useState('');
  const [instrument, setInstrument] = useState('');
  const [runAt, setRunAt] = useState('');
  const [error, setError] = useState<string | null>(null);

  const record = useMutation({
    mutationFn: () =>
      post<{ accepted: boolean; zScore: number; violations: string[] }>('/lab/qc/results', {
        controlLotId: lot.id,
        value: Number(value),
        instrument: instrument || undefined,
        runAt: runAtIso(runAt),
      }),
    onSuccess: (result) => {
      if (result.accepted) {
        toast.success(tr('lab', 'in_control'), tr('lab', 'x_x_sd', { value: result.zScore > 0 ? '+' : '', zScore: result.zScore }));
      } else {
        toast.error(
          tr('lab', 'out_of_control_do_not_report_this_run'),
          result.violations.map((rule) => WESTGARD_DESCRIPTIONS[rule as WestgardRule] ?? rule).join('; '),
        );
      }
      void queryClient.invalidateQueries({ queryKey: ['qc-chart'] });
      void queryClient.invalidateQueries({ queryKey: ['qc-gate'] });
      navigate(returnTo, { replace: true });
    },
    onError: (caught) => setError(runRefusal(caught)),
  });

  const z = value ? (Number(value) - lot.targetMean) / lot.targetSd : null;
  const dirty = !record.isSuccess && (value !== '' || instrument !== '' || runAt !== '');

  return (
    <FormScreen
      title={tr('lab', 'record_a_control_run')}
      description={tr('lab', 'x_x_lot_x', { name: lot.test.name, level: lot.level, lotNo: lot.lotNo })}
      eyebrow={tr('lab', 'quality_control')}
      icon={Activity}
      backTo={returnTo}
      dirty={dirty}
      onSubmit={(event) => {
        event.preventDefault();
        if (value === '') return;
        setError(null);
        record.mutate();
      }}
    >
      {lot.expired && (
        <Alert variant="warning">
          {tr('lab', 'this_lot_expired_on')} {dateOnly(lot.expiryDate)}
          {tr('lab', 'a_run_against_it_will_be_refused')}
        </Alert>
      )}

      <FormSection title={tr('lab', 'result')} icon={Activity}>
        <Field label={`${tr('lab', 'value')} ${lot.test.unit ? `(${lot.test.unit})` : ''}`} required>
          <Input type="number" step="any" value={value} onChange={(e) => setValue(e.target.value)} autoFocus />
        </Field>
        <Field label={tr('lab', 'instrument')}>
          <Input value={instrument} onChange={(e) => setInstrument(e.target.value)} placeholder={tr('lab', 'cobas_e411')} />
        </Field>
        <Field label={tr('lab', 'run_time')} hint={tr('lab', 'leave_empty_for_now_a_run_cannot_be_in_the')} className="sm:col-span-2">
          <DateTimePicker value={runAt} onChange={setRunAt} />
        </Field>
        {z !== null && Number.isFinite(z) && (
          <Alert variant={Math.abs(z) > 3 ? 'destructive' : Math.abs(z) > 2 ? 'warning' : 'success'} className="sm:col-span-2">
            {z > 0 ? '+' : ''}
            {Math.round(z * 100) / 100} {tr('lab', 'sd_from_the_target_mean')}
          </Alert>
        )}
      </FormSection>

      {error && <Alert variant="destructive">{error}</Alert>}

      <FormActions submitLabel={tr('lab', 'record')} loading={record.isPending} incomplete={value === ''} cancelTo={returnTo} />
    </FormScreen>
  );
}
